import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Play, Sparkles, Briefcase, Plane, Home, Info, BookOpen } from "lucide-react";
import type { EoTheme, EoProgress } from "@/types/eoTypes";
import { eoThemes } from "@/data/eoMockData";

// Helper to get an icon by name
const getIcon = (name: string, className?: string) => {
  switch (name) {
    case "Plane": return <Plane className={className} />;
    case "Briefcase": return <Briefcase className={className} />;
    case "Home": return <Home className={className} />;
    default: return <BookOpen className={className} />;
  }
};

export default function EoThemesGrid() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [progressData, setProgressData] = useState<Record<string, EoProgress>>({});

  useEffect(() => {
    // Load progress from localStorage for now
    try {
      const stored = localStorage.getItem("tef_eo_progress");
      if (stored) {
        setProgressData(JSON.parse(stored));
      }
    } catch (err) {
      console.error("Error loading EO progress", err);
    }
  }, []);

  const calculateProgress = (themeId: string, totalExercises: number) => {
    if (totalExercises === 0) return 0;
    const completed = progressData[themeId]?.completedExercises?.length || 0;
    return Math.min(100, Math.round((completed / totalExercises) * 100));
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="bg-gradient-to-br from-indigo-50 to-white border border-indigo-100 rounded-3xl p-6 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-2xl flex items-center justify-center shrink-0 shadow-inner">
            <Sparkles className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-800">100 Thèmes - Expression Orale</h2>
            <p className="text-slate-600 text-sm mt-1 max-w-2xl leading-relaxed">
              Pratiquez le vocabulaire des sujets les plus fréquents de l'examen. 
              Maîtrisez les expressions de la <span className="font-semibold text-indigo-700">Section A (Formel)</span> et de la <span className="font-semibold text-indigo-700">Section B (Informel)</span> avec des exercices interactifs.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {eoThemes.map((theme) => {
          const progressPercent = calculateProgress(theme.id, theme.exercisesCount);
          const isCompleted = progressPercent === 100;
          
          return (
            <Card key={theme.id} className="border-none shadow-sm hover:shadow-md transition-all duration-200 flex flex-col bg-white">
              <CardHeader className="pb-3 flex-row items-start justify-between space-y-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 text-slate-600 rounded-xl flex items-center justify-center shrink-0">
                    {getIcon(theme.iconName, "w-5 h-5")}
                  </div>
                  <Badge variant="outline" className={`font-bold text-[10px] uppercase border ${theme.category.includes('A') ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200'}`}>
                    {theme.category}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 flex-1">
                <div>
                  <CardTitle className="text-lg font-bold text-slate-800 leading-tight">
                    {theme.title}
                  </CardTitle>
                  <CardDescription className="text-sm mt-2 text-slate-500 font-medium">
                    {theme.description}
                  </CardDescription>
                </div>
                
                <div className="space-y-1.5 mt-auto">
                  <div className="flex justify-between text-xs font-bold">
                    <span className="text-slate-500">Progresso</span>
                    <span className={isCompleted ? "text-emerald-600" : "text-indigo-600"}>
                      {progressPercent}%
                    </span>
                  </div>
                  <Progress value={progressPercent} className={`h-2 ${isCompleted ? '[&>div]:bg-emerald-500' : '[&>div]:bg-indigo-600'} bg-slate-100`} />
                  <p className="text-[10px] font-semibold text-slate-400 text-right">
                    {progressData[theme.id]?.completedExercises?.length || 0} / {theme.exercisesCount} exercícios
                  </p>
                </div>
              </CardContent>
              <CardFooter className="pt-2 pb-5">
                <Button 
                  onClick={() => navigate(`/grammar/eo/${theme.id}`)}
                  className={`w-full font-bold shadow-sm rounded-xl ${isCompleted ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}
                >
                  {isCompleted ? (
                    <>Revisar Tema</>
                  ) : progressPercent > 0 ? (
                    <>Continuar <Play className="w-4 h-4 ml-2" /></>
                  ) : (
                    <>Começar <Play className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
              </CardFooter>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
